import { memo, useCallback } from 'react';
import { IconHighlight, ModalLayout } from '@aimsos/ui';
import { UserMinus } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';

import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { REMOVE_STEP, useRemoveMemberFlow } from '../../hooks/useRemoveMemberFlow';
import { NO_HEADER_LINE, WIDE_MODAL } from '../shared/modalClasses';
import RemoveMemberStep1 from './RemoveMemberStep1';
import RemoveMemberStep2 from './RemoveMemberStep2';

const TITLE_ID = 'remove-member-title';

/**
 * Two-step Remove from workspace dialog (soft delete): what they lose, then type the member's
 * email to confirm. The step is local state (`useRemoveMemberFlow`), not a wizard.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {object} props.member UI member (see `mapApiMemberToUi`)
 * @param {() => Promise<boolean>} props.onRemove Resolves `true` when removed (the dialog closes).
 * @param {() => void} props.onClose
 */
const RemoveMemberModal = ({ isOpen, member, onRemove, onClose }) => {
  const { t } = useTranslation('people-access');
  const flow = useRemoveMemberFlow({ member, isOpen, onRemove, onClose });

  // Esc closes unless the removal is in flight.
  const { isSubmitting } = flow;
  const handleEscape = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);
  useDialogKeyboard(isOpen, handleEscape, TITLE_ID);

  if (!isOpen) return null;

  const isFirstStep = flow.step === REMOVE_STEP.LOSES;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      useBodyPortal
      title={t('removeFlow.title', { name: member.displayName })}
      subtitle={isFirstStep ? t('removeFlow.step1') : t('removeFlow.step2', { email: member.email })}
      closeAriaLabel={t('removeFlow.close')}
      titleId={TITLE_ID}
      titlePrefix={<IconHighlight icon={UserMinus} bgColor="Error" iconColor="Error" size="medium" />}
      panelClassName={NO_HEADER_LINE}
      closeDisabled={flow.isSubmitting}
      maxWidthClassName={WIDE_MODAL}
    >
      {isFirstStep ? (
        <RemoveMemberStep1
          name={member.displayName}
          summary={flow.summary}
          isLoading={flow.isLoadingSummary}
          hasError={flow.summaryError}
          onCancel={onClose}
          onContinue={flow.next}
        />
      ) : (
        <RemoveMemberStep2
          email={member.email}
          value={flow.emailInput}
          isMatch={flow.isEmailMatch}
          isSubmitting={flow.isSubmitting}
          onChange={flow.setEmailInput}
          onBack={flow.back}
          onCancel={onClose}
          onConfirm={flow.confirm}
        />
      )}
    </ModalLayout>
  );
};

export default memo(RemoveMemberModal);
